"use client";
import { GameDemo } from "@/components/marketing";
import { Button } from "@/components/ui/button";
import type { WheelPrize } from "../../data";
import { PrizeWheel } from "./components";
import { useSpinWheel } from "./hooks";

interface SpinWheelDemoProps {
  ariaLabel: string;
  stepsLabel: string;
  steps: string[];
  wheel: {
    ariaLabel: string;
    centerLabel: string;
    spinLabel: string;
    prizes: WheelPrize[];
  };
  screen: {
    shop: string;
    game: string;
    waitTitle: string;
    waitText: string;
    spinningText: string;
    wonLabel: string;
    redeemLabel: string;
    doneTitle: string;
    doneText: string;
    againLabel: string;
  };
}

// The preview's spin_wheel demo: spin the wheel, see the offer on the
// customer's phone, redeem it for a counter code.
const SpinWheelDemo = ({
  ariaLabel,
  stepsLabel,
  steps,
  wheel,
  screen,
}: SpinWheelDemoProps) => {
  const {
    phase,
    rotation,
    prize,
    code,
    spin,
    redeem,
    spinAgain,
    spinButtonRef,
    redeemButtonRef,
    againButtonRef,
  } = useSpinWheel({ prizes: wheel.prizes });
  const isSpinning = phase === "spinning";
  const showSpinButton = phase === "idle" || isSpinning;

  return (
    <GameDemo
      ariaLabel={ariaLabel}
      steps={{
        labels: steps,
        ariaLabel: stepsLabel,
        activeIndex: phase === "offer" ? 1 : 0,
        isComplete: phase === "done",
      }}
      screen={{
        shop: screen.shop,
        game: screen.game,
        state: phase === "offer" ? "offer" : phase === "done" ? "done" : "wait",
        wait: {
          title: screen.waitTitle,
          text: isSpinning ? screen.spinningText : screen.waitText,
        },
        offer: {
          label: screen.wonLabel,
          title: prize.label,
          text: prize.description,
          action: (
            <Button
              ref={redeemButtonRef}
              type="button"
              variant="brand"
              size="pill"
              onClick={redeem}
            >
              {screen.redeemLabel}
            </Button>
          ),
        },
        done: {
          title: screen.doneTitle,
          text: screen.doneText.replace("{code}", code),
          action: (
            <Button
              ref={againButtonRef}
              type="button"
              variant="brandOutline"
              size="pillSm"
              className="bg-white"
              onClick={spinAgain}
            >
              {screen.againLabel}
            </Button>
          ),
        },
      }}
    >
      <PrizeWheel
        prizes={wheel.prizes}
        rotation={rotation}
        ariaLabel={wheel.ariaLabel}
        centerLabel={wheel.centerLabel}
      />
      {showSpinButton && (
        <Button
          ref={spinButtonRef}
          type="button"
          variant="brand"
          size="pill"
          disabled={isSpinning}
          onClick={spin}
        >
          {wheel.spinLabel}
        </Button>
      )}
    </GameDemo>
  );
};

export default SpinWheelDemo;
